import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGetWellLithologyVisualisationData } from '../../../hooks/use-api'
import useUser from '../../../hooks/use-user'
import { canEditWell } from '../../../services/user-utils'
import LithologyDiagramTab from './diagram-tab'
import { TabState } from './lithology-types'
import LithologySummaryTable from './summary-table'
import WellViewButtons from '../../wells/well-view-buttons'
import { useWellState } from '../../../context/well-context'
import useToastMessage from '../../../hooks/use-toast-message'
import { useWellId } from '../../../hooks/use-well-id'
import {
  Button,
  ButtonsContainer,
  Card,
  Heading,
  HeadingRow,
  RecordContent,
  RecordFooter,
  TabbedContent,
  useTitleChange,
} from '@msaf/core-react'

interface LithologyWrapperProps {
  isEditMode?: boolean
  isNewWell?: boolean
}

export default function LithologyWrapper({ isEditMode }: LithologyWrapperProps) {
  const { user } = useUser()
  const {
    wellState: { isDraft },
  } = useWellState()
  const [wellCanAddStratum, setWellCanAddStratum] = useState<boolean>()
  const [fields, setFields] = useState<Array<Record<string, string | number | boolean>> | null>(null)
  const navigate = useNavigate()
  const location = useLocation() as { state: TabState }
  const [activeTab, setActiveTab] = useState<number>(0)
  const [isDiagramError, setIsDiagramError] = useState(false)
  const [tabs, setTabs] = useState<
    {
      label: string
      tabContent: JSX.Element
    }[]
  >([])
  const id = useWellId()
  const { data: dataVisualisation } = useGetWellLithologyVisualisationData(`wells/${id}/lithology-visualisation`)
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Lithology`)

  useToastMessage({
    isError: isDiagramError,
    errorMessage: 'Lithology diagram could not be rendered',
  })

  useEffect(() => {
    const tabs = [
      {
        label: 'Details',
        tabContent: (
          <LithologySummaryTable
            fields={fields}
            setFields={setFields}
            setWellCanAddStratum={setWellCanAddStratum}
            isEditMode={isEditMode}
          />
        ),
      },
    ]
    if (fields?.length && !(fields.length === 1 && fields[0].stratumBottom === null) && !isDiagramError) {
      tabs.unshift({
        label: 'Diagram',
        tabContent: <LithologyDiagramTab lithologyJson={dataVisualisation} setIsDiagramError={setIsDiagramError} />,
      })
    }
    setTabs(tabs)
    if (location?.state?.activeTab != null && location.state.activeTab >= tabs.length) {
      setActiveTab(tabs.length - 1)
    } else if (location?.state?.activeTab != null) {
      setActiveTab(location.state.activeTab)
    }
  }, [fields, isEditMode, location?.state?.activeTab, dataVisualisation, isDraft, isDiagramError])

  // If there will never be strata added fields.hasNoStrataRecords will be true and the add button will be disabled.
  // If checkbox in edit is unchecked wellCanAddStraum will be true and the add button should be disabled.
  // Leaving this verbose as the logic was a little tricky.
  const isAddStrataButtonDisabled = () => {
    if (wellCanAddStratum === true) {
      return false
    }

    if (fields?.length) {
      const firstElement = fields[0]
      /**
       * Disable 'Add new' button if:
       * 1. User has declared that there are no strata records to add, or
       * 2. if stratumBottom is null. Checking just the first entry as
       * the UI should ideally not allow more strata records to be added
       * (The logic is still very brittle)
       */
      return !!firstElement.hasNoStrataRecords || (fields.length === 1 && firstElement.stratumBottom === null)
    }

    return false
  }

  return (
    <RecordContent>
      <Heading level={2}>Lithology</Heading>
      <Card isFullWidth hasTabbedContent>
        <TabbedContent
          activeTab={activeTab}
          borderBottomStyle='shadow'
          includeHorizontalPadding={true}
          ariaLabel='Edit lithology details'
          tabs={tabs}
          tabsHeading={
            <HeadingRow headingLevel={3} headingText='Strata' verticalSpacing='none'>
              {isEditMode && canEditWell(user) && (
                <ButtonsContainer
                  containerStyle='right'
                  tooltipMessage={
                    isAddStrataButtonDisabled()
                      ? 'Bottom strata layer must be entered to add additional strata'
                      : undefined
                  }
                >
                  <Button
                    label='Add new'
                    onClick={() =>
                      navigate(`/wells/${id}/edit/lithology/stratum/new`, { state: { isAddingNewStratum: true } })
                    }
                    isDisabled={isAddStrataButtonDisabled()}
                  />
                </ButtonsContainer>
              )}
            </HeadingRow>
          }
        />
      </Card>
      {!isEditMode && (
        <RecordFooter areActionsFixed>
          <WellViewButtons wellId={id} />
        </RecordFooter>
      )}
    </RecordContent>
  )
}
